import { motion } from 'framer-motion';
import { Plus, Minus } from 'lucide-react';
import { useState } from 'react';
import { FAQ } from '@/constants/testIds';

const ITEMS = [
	{
		q: 'How fast do you actually arrive?',
		a: 'ETAs depend on where you are — but our dispatcher gives you a real ETA on the call, not a guess. Urban areas average 20–40 minutes. Rural highways may take longer, but you’ll always have a live update.',
	},
	{
		q: 'Are you licensed and insured?',
		a: 'Yes. Every operator in our network is licensed for the service they provide and carries active commercial insurance. Ask us on the phone and we’ll walk you through it.',
	},
	{
		q: 'Do you cover my area?',
		a: 'We dispatch nationwide across all 50 states. Interstates, rural roads, suburbs — if it has a road, we cover it. Confirm your exact location on the call.',
	},
	{
		q: 'How much does it cost?',
		a: 'Pricing depends on the service (tow distance, winch complexity, etc.). We quote you the full price on the call before we roll — no hidden fees, no surprises when we arrive.',
	},
	{
		q: 'What information should I have ready when I call?',
		a: 'Your exact location (cross streets or a pinned GPS is best), the make and model of your vehicle, and a quick description of what happened. That’s it.',
	},
	{
		q: 'Do you tow motorcycles, RVs, or heavy trucks?',
		a: 'Yes — we have operators equipped for light-duty cars, motorcycles, RVs, and medium-duty vehicles. Mention your vehicle type on the call so we send the right rig.',
	},
	{
		q: 'What payment methods do you accept?',
		a: 'Cash, all major credit and debit cards, and mobile pay. Payment is collected on completion.',
	},
];

const Row = ({ item, i }) => {
	const [open, setOpen] = useState(false);
	return (
		<div
			data-testid={FAQ.item(i)}
			className="border-t border-brand-navy"
		>
			<button
				type="button"
				onClick={() => setOpen((o) => !o)}
				className="w-full text-left flex items-start justify-between gap-6 py-6 sm:py-8 group"
				aria-expanded={open}
			>
				<div className="flex items-start gap-6 sm:gap-8">
					<span className="font-anton text-brand-red text-2xl sm:text-3xl leading-none pt-1">
						{String(i + 1).padStart(2, '0')}
					</span>
					<span className="font-chivo font-black uppercase text-brand-navy text-xl sm:text-2xl md:text-3xl leading-tight tracking-tight">
						{item.q}
					</span>
				</div>
				<span className="mt-1 w-10 h-10 sm:w-12 sm:h-12 flex-shrink-0 border border-brand-navy flex items-center justify-center group-hover:bg-brand-navy group-hover:text-white transition-colors">
					{open ? (
						<Minus className="w-4 h-4" strokeWidth={2.5} />
					) : (
						<Plus className="w-4 h-4" strokeWidth={2.5} />
					)}
				</span>
			</button>
			<motion.div
				initial={false}
				animate={{
					height: open ? 'auto' : 0,
					opacity: open ? 1 : 0,
				}}
				transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
				className="overflow-hidden"
			>
				<div className="pb-8 pl-0 sm:pl-16 max-w-3xl font-plex text-brand-navy/75 text-[15px] sm:text-base leading-relaxed">
					{item.a}
				</div>
			</motion.div>
		</div>
	);
};

export const FaqSection = () => {
	return (
		<section
			id="faq"
			data-testid={FAQ.root}
			className="bg-brand-surface"
		>
			<div className="mx-auto max-w-[1400px] px-4 sm:px-8 py-24 sm:py-32">
				<div className="grid md:grid-cols-12 gap-8 mb-12 sm:mb-16">
					<div className="md:col-span-4">
						<div className="font-chivo text-xs uppercase tracking-[0.3em] text-brand-red font-bold mb-4">
							/ Frequently Asked
						</div>
					</div>
					<div className="md:col-span-8">
						<motion.h2
							initial={{ opacity: 0, y: 30 }}
							whileInView={{ opacity: 1, y: 0 }}
							viewport={{ once: true }}
							transition={{ duration: 0.7 }}
							className="font-anton text-[38px] sm:text-[64px] md:text-[84px] leading-[0.92] text-brand-navy"
						>
							Answers, before{' '}
							<span className="text-brand-red">you dial.</span>
						</motion.h2>
					</div>
				</div>

				<div className="border-b border-brand-navy">
					{ITEMS.map((item, i) => (
						<Row key={item.q} item={item} i={i} />
					))}
				</div>
			</div>
		</section>
	);
};

export default FaqSection;
